import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabaseClient";
import { toast } from "sonner";
import { useState } from "react";

export interface CalendarIntegration {
  id: string;
  provider: string;
  account_email: string;
  status: string;
}

export type CalendarProvider = 'google' | 'outlook' | 'calendly' | 'acuity' | 'square' | 'ghl';

// Simple hook to check localStorage for integration existence
export const useIntegrationLocalStorage = () => {
  const [hasIntegrationInStorage, setHasIntegrationInStorage] = useState<boolean>(() => {
    return localStorage.getItem("calendar-integration-exists") === "true";
  });

  const updateIntegrationStorage = (exists: boolean) => {
    if (exists) {
      localStorage.setItem("calendar-integration-exists", "true");
    } else {
      localStorage.removeItem("calendar-integration-exists");
    }
    setHasIntegrationInStorage(exists);
  };

  return {
    hasIntegrationInStorage,
    updateIntegrationStorage,
  };
};

// Helper function to create auth headers
const createAuthHeaders = async (additionalHeaders: Record<string, string> = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error("Not authenticated");
  
  return {
    Authorization: `Bearer ${session.access_token}`,
    ...additionalHeaders,
  };
};

// Hook to get active integration (single integration or null)
export const useActiveIntegration = () => {
  const { updateIntegrationStorage } = useIntegrationLocalStorage();

  return useQuery<CalendarIntegration | null>({
    queryKey: ["calendar-integrations"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: integrations, error } = await supabase
        .from("calendar_integrations")
        .select("id, provider, account_email, status")
        .eq("user_id", user.id);

      if (error) throw error;
      
      const activeIntegration = integrations && integrations.length > 0 ? integrations[0] : null;

      updateIntegrationStorage(!!activeIntegration);
      return activeIntegration;
    },
    refetchOnWindowFocus: false,
  });
};

// Hook to connect calendar integration
export const useConnectCalendar = () => {
  const { updateIntegrationStorage } = useIntegrationLocalStorage();

  return useMutation({
    mutationFn: async (provider: CalendarProvider) => {
      const headers = await createAuthHeaders({ "Content-Type": "application/json" });

      const currentPath = window.location.pathname;
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/calendar-integration`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({ provider, returnPath: currentPath }),
        }
      );

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || `Failed to connect ${provider}`);
      }

      return data.url as string;
    },
    onSuccess: (url) => {
      // Update localStorage before redirect
      updateIntegrationStorage(true);
      window.location.href = url;
    },
    onError: (err: any) => {
      toast.error(`Connect failed: ${err.message}`);
    },
  });
};

// Hook to disconnect calendar integration
export const useDisconnectCalendar = () => {
  const queryClient = useQueryClient();
  const { updateIntegrationStorage } = useIntegrationLocalStorage();

  return useMutation({
    mutationFn: async (integrationId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase
        .from("calendar_integrations")
        .delete()
        .eq("id", integrationId)
        .eq("user_id", user.id);

      if (error) throw error;
      return { success: true };
    },
    onSuccess: () => {
      queryClient.setQueryData(["calendar-integrations"], null);
      queryClient.invalidateQueries({ queryKey: ["calendar-integrations"] });
      updateIntegrationStorage(false);
      toast.success("Calendar disconnected successfully.");
    },
    onError: (err: any) => {
      toast.error(`Disconnect failed: ${err.message}`);
    },
  });
};
